import { apiDeleteResult, apiGet, apiPatchResult, apiPostResult } from '@/services/api/http'

/** Website enquiries waiting in the Leads Inbox (W-n) until added to Queries or rejected. */
export type SiteInboxStatus = 'pending' | 'added' | 'rejected'
export type SiteInboxRejectReason = 'spam' | 'duplicate' | 'test' | 'other'

export interface SiteInboxLine {
  description: string
  quantity: number | null
  categoryId: string | null
  categoryNameSnapshot: string | null
}

export interface SiteInboxItem {
  id: string
  enquiryNo: string
  status: SiteInboxStatus
  channel: string
  channelLabel: string
  businessName: string
  contactName: string
  phone: string
  email: string | null
  cityId: string | null
  cityName: string
  message: string | null
  quantityText: string | null
  lines: SiteInboxLine[]
  products: string
  totalQuantity: number
  assignedUserId: string | null
  rejectReason: SiteInboxRejectReason | null
  rejectReasonLabel: string | null
  rejectNote: string | null
  leadId: string | null
  leadNo: string | null
  addedAs: 'new' | 'note' | null
  reviewedBy: string | null
  reviewedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface SiteInboxDetail extends SiteInboxItem {
  city: { id: string; name: string; province: string; provinceLabel: string | null } | null
  /** Today's rule (territory manager → round-robin); null = the reviewer picks. */
  suggestedAssignee: { id: string; name: string; how: 'territory' | 'loop' | 'fallback' } | null
  /** Same customer's recent query (same phone + company). */
  repeatOf: { leadId: string; leadNo: string; businessName: string; assignedTo: string } | null
  samePhoneWaiting: { id: string; enquiryNo: string }[]
}

export interface SiteInboxCounts {
  pending: number
  added: number
  rejected: number
}

export interface SiteInboxList {
  items: SiteInboxItem[]
  total: number
  page: number
  pageSize: number
  counts: SiteInboxCounts
}

export interface SiteInboxListParams {
  status: SiteInboxStatus
  search?: string
  page?: number
  pageSize?: number
}

export interface SiteInboxEditInput {
  businessName?: string
  contactName?: string
  phone?: string
  email?: string | null
  cityId?: string | null
  message?: string | null
  assignedUserId?: string | null
  lines?: { description: string; quantity: number | null; categoryId: string | null }[]
}

export interface SiteInboxAddInput extends SiteInboxEditInput {
  mode: 'new' | 'note'
  leadId?: string
  businessType?: string | null
}

export const siteInboxService = {
  list: (params: SiteInboxListParams) => apiGet<SiteInboxList>('/site-form/inbox', { params }),
  get: (id: string) => apiGet<SiteInboxDetail>(`/site-form/inbox/${id}`),
  save: async (id: string, input: SiteInboxEditInput) =>
    (await apiPatchResult<SiteInboxDetail>(`/site-form/inbox/${id}`, input)).data,
  add: async (id: string, input: SiteInboxAddInput) =>
    (await apiPostResult<SiteInboxDetail>(`/site-form/inbox/${id}/add`, input)).data,
  reject: async (id: string, input: { reason: SiteInboxRejectReason; note?: string | null }) =>
    (await apiPostResult<SiteInboxDetail>(`/site-form/inbox/${id}/reject`, input)).data,
  restore: async (id: string) => (await apiPostResult<SiteInboxDetail>(`/site-form/inbox/${id}/restore`)).data,
  delete: async (id: string) => {
    await apiDeleteResult(`/site-form/inbox/${id}`)
  },
}
